// ═══════════════════════════════════════════════════════════════════════════════
// returnTicket.js — LEAN-04-FU2: seamless return from the standalone /screener/
// Intelligence Portal without a re-login.
//
// The app's auth is in-memory by design (App.jsx) — a full reload (which is how
// /screener/ ← → the React app navigate) drops it, landing the admin back on the
// login screen. This stashes a *short-lived, one-shot* return ticket so the next
// boot can rehydrate the same session.
//
// SECURITY (the deliberate tradeoff Oliver accepted):
//   • sessionStorage only — per-tab, cleared when the tab closes. NOT localStorage,
//     so nothing persists across browser sessions. Same surface already used to hand
//     the admin JWT to /screener/ (`sykes_intel_token`); no new long-term storage.
//   • short TTL (15 min) — bounds the window; an expired ticket falls back to login.
//   • one-shot — consumed (removed) on first read, so it can't be replayed and a
//     later manual refresh won't silently resurrect the session.
//   • only written when leaving for /screener/ (an admin-only action).
// ═══════════════════════════════════════════════════════════════════════════════
const KEY = 'sykes_return_ticket';
const TTL_MS = 15 * 60 * 1000; // 15 min — covers a real screener round-trip, brief by design

// Stash { token, user, session } so the next app boot can rehydrate. No-op if the
// auth is incomplete or sessionStorage is unavailable.
export function saveReturnTicket(auth) {
  if (!auth || !auth.token) return;
  try { sessionStorage.setItem(KEY, JSON.stringify({ auth, exp: Date.now() + TTL_MS })); } catch {}
}

// Return the saved auth ({ token, user, session }) if a valid, unexpired ticket
// exists, else null. ALWAYS clears the ticket (one-shot), even when expired/invalid.
export function consumeReturnTicket() {
  let raw = null;
  try { raw = sessionStorage.getItem(KEY); sessionStorage.removeItem(KEY); } catch {}
  if (!raw) return null;
  try {
    const t = JSON.parse(raw);
    if (t && t.auth && t.auth.token && typeof t.exp === 'number' && Date.now() <= t.exp) return t.auth;
  } catch {}
  return null;
}
